/**
 * What the page can know about the sign in before Clerk has finished loading.
 *
 * Clerk's script needs two round trips to its own servers before it can say who
 * is here, and until it does the portal had nothing to show but a blank sheet:
 * about a second on a good connection, several on a phone on mobile data. Two
 * cookies Clerk keeps on this very site answer most of the question at once:
 *
 *   __client_uat   "0" when nobody is signed in, a timestamp when somebody is
 *   __session      the session token itself, valid for about a minute, which
 *                  clerk-js keeps fresh for as long as a tab is open
 *
 * Both are readable by the page (Clerk's own documentation says so, they are
 * how its backend SDKs read a session), so reading them adds no exposure the
 * script did not already have. They are hints, never authority. The token is
 * sent to the API, which verifies it exactly as it verifies one from
 * clerk.session.getToken(), and a stale or forged one gets the same 401. The
 * signed out hint only decides what to draw first: the gate still asks Clerk
 * and corrects the page if the hint was wrong.
 *
 * In development Clerk suffixes the names with the instance (`__session_xxx`),
 * so a name is matched with or without a suffix.
 */

export interface Hint {
  /** A session token with enough life left to use, or null. */
  readonly token: string | null;
  /** Nobody is signed in here, as far as Clerk's own cookie says. */
  readonly signedOut: boolean;
}

/** A token this close to expiry is not worth the round trip: it may lapse in flight. */
const MIN_REMAINING_SECONDS = 10;

function cookies(raw: string): Map<string, string> {
  const found = new Map<string, string>();
  for (const part of raw.split(";")) {
    const at = part.indexOf("=");
    if (at < 0) continue;
    found.set(part.slice(0, at).trim(), part.slice(at + 1).trim());
  }
  return found;
}

/** The value of a cookie by its plain name, else by any instance suffixed name. */
function named(jar: Map<string, string>, name: string): string | undefined {
  const plain = jar.get(name);
  if (plain !== undefined) return plain;
  for (const [key, value] of jar) if (key.startsWith(`${name}_`)) return value;
  return undefined;
}

/** The `exp` of a JWT, in seconds, or null for anything that is not one. */
export function expiryOf(token: string): number | null {
  const parts = token.split(".");
  if (parts.length !== 3 || !parts[1]) return null;
  try {
    const padded = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = atob(padded + "=".repeat((4 - (padded.length % 4)) % 4));
    const exp = (JSON.parse(json) as { exp?: unknown }).exp;
    return typeof exp === "number" && Number.isFinite(exp) ? exp : null;
  } catch {
    return null;
  }
}

export function readHint(raw: string = typeof document === "undefined" ? "" : document.cookie, nowMs: number = Date.now()): Hint {
  const jar = cookies(raw);
  const uat = named(jar, "__client_uat");
  // Absent is as good as "0": a browser Clerk has never run in has nobody
  // signed in, and one it has run in always has the cookie.
  if (uat === undefined || uat === "0" || uat === "") return { token: null, signedOut: true };

  const token = named(jar, "__session");
  const exp = token ? expiryOf(token) : null;
  const live = token !== undefined && exp !== null && exp - nowMs / 1000 > MIN_REMAINING_SECONDS;
  return { token: live ? (token as string) : null, signedOut: false };
}
